import {
  ArrowLeftOutlined,
  FileProtectOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Controller,
  FormProvider,
  useController,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QUOTE_STATUS_SELECT_OPTIONS } from "../../../shared/constants";
import { downloadFile } from "../../../shared/downloadFile";
import { clearQuote } from "../../../store/quotes/quotesSlice";
import { fetchQuote, saveQuote } from "../../../store/quotes/quotesThunks";
import ApplicationHeaderBar from "../shared/ApplicationHeaderBar";
import QuoteOptions from "./QuoteOptions";
import PolicyDateFields from "./PolicyDateFields";
import { cloneDeep } from "lodash";
const { Text } = Typography;

const ApplicationsQuote = () => {
  //
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId } = useParams();

  //
  const {
    control,
    reset,
    watch,
    trigger,
    getValues,
    setValue,
    setFocus,
    setError,
    clearErrors,
    formState: { isDirty },
  } = useForm({
    mode: "onChange",
    defaultValues: {},
  });

  //
  const { field: select } = useController({ name: "status", control });

  const { field: approvedPilotsInput } = useController({
    name: "approved_pilots",
    control,
  });

  const { field: underwritingComments } = useController({
    name: "underwriting_comments",
    control,
  });

  const { field: internalComments } = useController({
    name: "internal_comments",
    control,
  });

  const { field: customerCodeInput } = useController({
    name: "customer_code",
    control,
  });

  //
  const data = useSelector((state) => state.quotes.current.data);
  const isLoading = useSelector((state) => state.quotes.current.isLoading);
  const isSaving = useSelector((state) => state.quotes.current.isSaving);
  const isDownloading = useSelector((state) => state.downloads.isDownloading);
  const isGenerateQuoteClicked = useRef(false);

  //
  useEffect(() => {
    dispatch(fetchQuote({ id: applicationId }));
    return () => {
      dispatch(clearQuote());
    };
  }, []);

  useEffect(() => {
    if (data) {
      reset(data);
    }
  }, [data]);

  // When a quote is Bound both policy dates are required and the expiry must be
  // strictly after the effective date. We derive the errors from the current form
  // values (single source of truth), push them into react-hook-form so the date
  // pickers render inline messages, and use `policyDatesInvalid` to gate Save /
  // Generate Quote below. Leaving Bound keeps the dates (we never clear them).
  const status = watch("status");
  const isBound = status === "Bound";
  const policyEffectiveDate = watch("policy_effective_date");
  const policyExpiryDate = watch("policy_expiry_date");

  const policyDateErrors = useMemo(() => {
    const errors = { effective: null, expiry: null };
    if (!isBound) return errors;
    if (!policyEffectiveDate) {
      errors.effective = "Policy effective date is required.";
    }
    if (!policyExpiryDate) {
      errors.expiry = "Policy expiry date is required.";
    } else if (
      policyEffectiveDate &&
      !dayjs(policyExpiryDate).isAfter(dayjs(policyEffectiveDate), "day")
    ) {
      errors.expiry = "Policy expiry date must be after the effective date.";
    }
    return errors;
  }, [isBound, policyEffectiveDate, policyExpiryDate]);

  const policyDatesInvalid = Boolean(
    policyDateErrors.effective || policyDateErrors.expiry
  );

  useEffect(() => {
    if (policyDateErrors.effective) {
      setError("policy_effective_date", {
        type: "manual",
        message: policyDateErrors.effective,
      });
    } else {
      clearErrors("policy_effective_date");
    }
    if (policyDateErrors.expiry) {
      setError("policy_expiry_date", {
        type: "manual",
        message: policyDateErrors.expiry,
      });
    } else {
      clearErrors("policy_expiry_date");
    }
  }, [policyDateErrors, setError, clearErrors]);

  // Convenience default: when the user sets the effective date while the expiry
  // is still blank, derive the expiry as exactly one year later. We never
  // overwrite an expiry that already has a value — one the user typed or one
  // restored from a previously bound quote — so this only ever offers a helpful
  // default, never clobbers a deliberate choice. (Clearing the expiry re-enables
  // the default.) Reacts only to user input (type "change"), so the programmatic
  // reset() that loads a quote is ignored.
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (name !== "policy_effective_date" || type !== "change") return;
      const effective = values.policy_effective_date;
      const expiry = values.policy_expiry_date;
      if (!effective || expiry) return;
      setValue(
        "policy_expiry_date",
        dayjs(effective).add(1, "year").format("YYYY-MM-DD"),
        { shouldDirty: true }
      );
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  const saveData = async () => {
    var data = getValues();
    var result = await dispatch(saveQuote({ id: data.id, data: data }));
    return result?.meta?.requestStatus === "fulfilled" ?? false;
  };

  const {
    fields: additionalFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "additional_aircraft",
  });

  const [editingIndex, setEditingIndex] = useState(null);
  const [prevEditValue, setPrevEditValue] = useState(null);
  const startAddAdditionalAircraft = async () => {
    // prevent starting another edit/add while one is in progress
    if (editingIndex !== null) return;
    const index = additionalFields.length;
    append({
      year: "",
      make: "",
      model: "",
      registration: "",
      passenger_seats: null,
    });
    // set the new appended entry into edit mode
    setEditingIndex(index);
    // focus the year field when it appears
    setTimeout(() => {
      setFocus(`additional_aircraft[${index}].year`);
    }, 50);
  };

  const startEditAircraft = (index) => {
    if (editingIndex !== null) return;
    setPrevEditValue(cloneDeep(getValues().additional_aircraft[index]));
    setEditingIndex(index);
    setTimeout(() => {
      setFocus(`additional_aircraft[${index}].year`);
    }, 50);
  };

  const cancelEdit = (index, isNew) => {
    if (isNew) {
      remove(index);
    } else if (prevEditValue) {
      setValue(`additional_aircraft[${index}]`, prevEditValue);
    }

    setPrevEditValue(null);
    setEditingIndex(null);
  };

  const additionalValues = watch("additional_aircraft") || [];

  const lastQuoted = data?.last_quoted_on
    ? `${dayjs(data.last_quoted_on).format("MMM D, YYYY hh:mmA")}
            ${data?.last_quoted_by ? " • " + data?.last_quoted_by : ""}`
    : "-";
  const headerActions = [
    location.key !== "default" &&
      (isDirty ? (
        <Popconfirm
          title={"Unsaved changes"}
          description={
            "You have unsaved changes. Are you sure you want to go back?"
          }
          onConfirm={() => navigate(-1)}
          okText="Yes"
          cancelText="No"
        >
          <Button size="small">
            <ArrowLeftOutlined /> Go Back
          </Button>
        </Popconfirm>
      ) : (
        <Button size="small" onClick={() => navigate(-1)}>
          <ArrowLeftOutlined /> Go Back
        </Button>
      )),
    <Button
      size="small"
      disabled={!isDirty}
      onClick={() => {
        reset(data);
        setPrevEditValue(null);
        setEditingIndex(null);
      }}
    >
      <ReloadOutlined /> Restore
    </Button>,
    <Button
      size="small"
      disabled={!isDirty || isDownloading || policyDatesInvalid}
      onClick={saveData}
    >
      <SaveOutlined /> Save
    </Button>,
    <Button
      type="primary"
      size="small"
      disabled={isDownloading || policyDatesInvalid}
      loading={isDownloading && isGenerateQuoteClicked.current}
      onClick={async () => {
        // Save data if there are changes
        if (isDirty) {
          const isSuccess = await saveData();
          if (!isSuccess) return;
        }

        // Generate quote .pdf
        const quoteId = getValues().id;
        isGenerateQuoteClicked.current = true;
        dispatch(
          downloadFile({
            url: `/private_aviation/quotes/${quoteId}/serve_pdf`,
            defaultName: "Quote",
          })
        );
      }}
    >
      <FileProtectOutlined /> Generate Quote
    </Button>,
    <Dropdown
      menu={{
        items: [
          {
            label: "Latest",
            key: "latest",
          },
          {
            label: "Signed",
            key: "signed",
          },
        ],
        onClick: ({ key }) => {
          if (key === "latest") {
            dispatch(
              downloadFile({
                url: `/private_aviation/forms/${applicationId}/serve_pdf`,
              })
            );
          } else if (key === "signed") {
            dispatch(
              downloadFile({
                url: `/private_aviation/forms/${applicationId}/serve_submitted_pdf`,
              })
            );
          }
        },
      }}
      trigger={["click"]}
    >
      <Button
        key="download"
        size="small"
        type="dashed"
        onClick={(e) => e.preventDefault()}
      >
        <PrinterOutlined /> Print application
      </Button>
    </Dropdown>,
  ];
  return data == null ? (
    <Alert
      message={`Error: Something went wrong while loading this quote!`}
      type="error"
      showIcon
      className="default-margin"
    />
  ) : (
    <Card className="body-card-content-no-min-height">
      <Skeleton active loading={isLoading}>
        <Spin spinning={isSaving}>
          <ApplicationHeaderBar data={data} actions={headerActions} />
          <Card
            size="small"
            style={{ marginBottom: 12, background: "#fafafa" }}
            bodyStyle={{ padding: 8 }}
          >
            <Row gutter={[16, 8]} align="middle" justify="space-between">
              <Col>
                <Space size={6}>
                  <Text type="secondary">Application #:</Text>{" "}
                  <Text>{data.application_number ?? "—"}</Text>
                </Space>
              </Col>

              <Col>
                <Text type="secondary">Last quoted:</Text>{" "}
                <Text>{lastQuoted}</Text>
              </Col>
              <Col>
                <Button
                  style={{ paddingLeft: 0, paddingRight: 0 }}
                  key="quote"
                  size="small"
                  type="link"
                  onClick={() => {
                    navigate(`/applications/view/${applicationId}`);
                  }}
                >
                  Edit Application
                </Button>
              </Col>
            </Row>
          </Card>
          <Row justify="space-between" style={{ marginBottom: "24px" }}>
            <Col span={24}>
              <Typography.Text strong>List of Aircraft:</Typography.Text>
            </Col>

            {/* Primary aircraft shown first as simple text */}
            <Col span={24} style={{ marginTop: 8 }}>
              <div>
                <Text>
                  1.{" "}
                  {`${data.year ?? "-"} ${data.make ?? "-"} ${
                    data.model ?? "-"
                  } (${data.registration ?? "-"}), Passenger Seats: ${
                    data.passenger_seats ?? "-"
                  }`}
                </Text>
              </div>
            </Col>

            {/* Saved additional aircraft shown as text with Edit/Remove; editable row appears inline */}
            <Col span={24} style={{ marginTop: 0 }}>
              <div style={{ marginTop: 0 }}>
                {additionalValues.length === 0 && (
                  <div style={{ color: "rgba(0,0,0,0.45)" }}>
                    No additional aircraft
                  </div>
                )}
                {additionalValues.map((av, idx) => {
                  const isEditing = editingIndex === idx;
                  const isNew =
                    av &&
                    !av.year &&
                    !av.make &&
                    !av.model &&
                    !av.registration &&
                    (av.passenger_seats === null ||
                      av.passenger_seats === undefined) &&
                    isEditing;
                  return (
                    <div
                      key={additionalFields[idx]?.id ?? idx}
                      style={{ marginTop: 0 }}
                    >
                      {!isEditing ? (
                        <Row align="middle" justify="space-between">
                          <Col>
                            <Text>
                              {idx + 2}.{" "}
                              {`${av?.year ?? "-"} ${av?.make ?? "-"} ${
                                av?.model ?? "-"
                              } (${
                                av?.registration ?? "-"
                              }), Passenger Seats: ${
                                av?.passenger_seats ?? "-"
                              }`}
                            </Text>
                          </Col>
                          <Col>
                            <Space>
                              <Button
                                type="link"
                                onClick={() => startEditAircraft(idx)}
                                disabled={editingIndex !== null}
                                size="small"
                              >
                                Edit
                              </Button>
                              <Button
                                type="link"
                                danger
                                onClick={() => remove(idx)}
                                disabled={editingIndex !== null}
                                size="small"
                              >
                                Remove
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      ) : (
                        <Card
                          size="small"
                          style={{ background: "#fafafa" }}
                          bodyStyle={{ pdataing: 10 }}
                        >
                          <Space>
                            <Controller
                              control={control}
                              name={`additional_aircraft[${idx}].year`}
                              render={({ field }) => (
                                <InputNumber
                                  {...field}
                                  placeholder="Year"
                                  maxLength={4}
                                  minLength={4}
                                  size="small"
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name={`additional_aircraft[${idx}].make`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="Make"
                                  size="small"
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name={`additional_aircraft[${idx}].model`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="Model"
                                  size="small"
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name={`additional_aircraft[${idx}].registration`}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  placeholder="Registration"
                                  size="small"
                                />
                              )}
                            />
                            <Controller
                              control={control}
                              name={`additional_aircraft[${idx}].passenger_seats`}
                              render={({ field }) => (
                                <InputNumber
                                  {...field}
                                  placeholder="Seats"
                                  min={0}
                                  size="small"
                                  style={{ width: "100%" }}
                                />
                              )}
                            />
                            <Button
                              type="link"
                              onClick={() => {
                                setPrevEditValue(null);
                                setEditingIndex(null);
                              }}
                              size="small"
                            >
                              {isNew ? "Add" : "Save"}
                            </Button>
                            <Button
                              onClick={() => cancelEdit(idx, isNew)}
                              size="small"
                              danger
                              type="link"
                            >
                              Cancel
                            </Button>
                          </Space>
                        </Card>
                      )}
                    </div>
                  );
                })}

                <div style={{ marginTop: 10 }}>
                  <Button
                    type="link"
                    onClick={startAddAdditionalAircraft}
                    disabled={editingIndex !== null}
                  >
                    <PlusOutlined /> Add additional aircraft
                  </Button>
                </div>
              </div>
            </Col>

            <Col flex="0 1 700px" style={{ marginTop: 16 }}>
              <Row justify={"start"}>
                <Col span={24}>
                  <Typography.Text strong>Approved Pilots: </Typography.Text>
                  <Input.TextArea
                    {...approvedPilotsInput}
                    rows={2}
                    placeholder="Type here"
                    style={{ marginBottom: 16 }}
                  />
                </Col>

                <Col span={24}>
                  <Typography.Text strong>
                    Underwriting Comments (will display):{" "}
                  </Typography.Text>
                  <Input.TextArea
                    {...underwritingComments}
                    rows={2}
                    maxLength={260}
                    showCount={true}
                    placeholder="Type here (max 260 characters)"
                    style={{ marginBottom: 16 }}
                  />
                </Col>
                <Col span={24}>
                  <Typography.Text strong>
                    Internal Comments (will not display):{" "}
                  </Typography.Text>
                  <Input.TextArea
                    {...internalComments}
                    rows={3}
                    placeholder="Type here"
                  />
                </Col>
              </Row>
            </Col>
            <Col flex="0 1 250px">
              <Row justify={"start"}>
                <Col span={24}>
                  <Typography.Text strong>Status: </Typography.Text>
                </Col>
                <Col span={24}>
                  <Select
                    {...select}
                    placeholder="Status"
                    size="small"
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: "250px", marginBottom: 16 }}
                    options={QUOTE_STATUS_SELECT_OPTIONS}
                  />
                </Col>
                {isBound && <PolicyDateFields control={control} />}
                <Col span={24}>
                  <Typography.Text strong>Customer Code:</Typography.Text>
                </Col>
                <Col span={24}>
                  <Input
                    size="small"
                    {...customerCodeInput}
                    placeholder="Type here"
                    style={{ maxWidth: "250px" }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <FormProvider
            control={control}
            trigger={trigger}
            watch={watch}
            setValue={setValue}
            getValues={getValues}
          >
            <QuoteOptions
              setFocus={setFocus}
              control={control}
              getValues={getValues}
              setValue={setValue}
            />
          </FormProvider>
        </Spin>
      </Skeleton>
    </Card>
  );
};

export default ApplicationsQuote;
